/* eslint-disable max-len */
import React, { useCallback, useState, useRef } from 'react';
import { Spinner, Size } from '@vtblife/uikit/legacy';
import classname from '@search/classname/src';
import { ICustomerFull } from '../../types/ICustomer';
import { useProfAgentContext } from '../../AgentFavoritesProvider/AgentFavoritesProvider';
import { AgentFavoritesLikeItem } from '../AgentFavoritesLikeItem/AgentFavoritesLikeItem';
import { AgentCreateCustomerItem } from '../AgentCreateCustomerItem/AgentCreateCustomerItem';

import './AgentFavoritesLikeList.css';

interface IOfferLikeList {
    offerId: string;
    fullWidth?: boolean;
    limitHeight?: boolean;
    isMyFavorite: boolean;
    onChangeMyFavorite: (v: boolean) => void;
}

const cn = classname.bind(null, 'AgentFavoritesLikeList');

export const AgentFavoritesLikeList: React.FC<IOfferLikeList> = ({
    offerId,
    limitHeight,
    isMyFavorite,
    fullWidth,
    onChangeMyFavorite
}) => {
    const listRef = useRef<HTMLDivElement>(null);

    const {
        customers,
        handleAddCustomer,
        handleOnChangeBusinessLike
    } = useProfAgentContext();

    if ((customers ?? null) === null) {
        return (<div className={cn()}>
            <div className={cn('loader')}>
                <Spinner size={Size.Medium} />
            </div>
        </div>);
    }

    return (
        <div className={cn(null, { fullWidth })} data-test={'offer-like-list'}>
            <div className={cn('wrap', { limitHeight })} ref={listRef}>
                <div className={cn('header')}>
                    Вы можете добавить избранное себе или нескольким клиентам:
                </div>
                <OfferLikeItemAgent
                    onChangePersonalLike={(isLike) => {
                        onChangeMyFavorite(isLike);

                        return Promise.resolve(true);
                    }}
                    isPersonalLike={isMyFavorite}
                />
                {Object.keys((customers ?? {})).map(customerId => {
                    return (
                        <OfferLikeItemCustomer
                            key={customerId}
                            offerId={offerId}
                            onChangeBusinessLike={(cId: string, v: boolean) => handleOnChangeBusinessLike(offerId, cId, v)}
                            customer={(customers ?? {})[customerId]}
                        />
                    );
                })}
            </div>
            <AgentCreateCustomerItem onAddCustomer={(data) => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                handleAddCustomer(offerId, data)
                    .then(() => {
                        if (listRef.current !== null) {
                            listRef.current.scrollTop = 0;
                        }
                    });
            }} />
        </div>
    );
};

interface IOfferLikeItemAgent {
    isPersonalLike: boolean;
    onChangePersonalLike: (isLike: boolean) => Promise<void | unknown>;
}

const OfferLikeItemAgent: React.FC<IOfferLikeItemAgent> = ({
    isPersonalLike,
    onChangePersonalLike
}) => {
    const [ isLike, setIsLike ] = useState(isPersonalLike);

    const handleOnChangePersonalLike = useCallback(() => {
        setIsLike(! isLike);
        onChangePersonalLike(! isLike)
            .catch(() => {
                setIsLike(isLike); // revert
            });
    }, [ isLike, onChangePersonalLike ]);

    return (
        <AgentFavoritesLikeItem
            isLike={isLike}
            name='Мои избранные'
            onChangeLike={handleOnChangePersonalLike}
        />
    );
};

interface IOfferLikeItemCustomer {
    offerId: string;
    onChangeBusinessLike: (customerId: string, isLike: boolean) => Promise<void | unknown>;
    customer: ICustomerFull;
}

const OfferLikeItemCustomer: React.FC<IOfferLikeItemCustomer> = ({
    offerId,
    onChangeBusinessLike,
    customer
}) => {
    const [ isBusinessLike, setIsBusinessLike ] = useState(customer.favoritesList.includes(offerId));

    const handleOnChangeBusinessLike = useCallback(() => {
        setIsBusinessLike(! isBusinessLike);
        onChangeBusinessLike(customer.id, isBusinessLike)
            .catch(() => {
                setIsBusinessLike(isBusinessLike); // revert
            });
    }, [ customer.id, isBusinessLike, onChangeBusinessLike ]);

    return (
        <AgentFavoritesLikeItem
            isLike={isBusinessLike}
            name={customer.name}
            onChangeLike={handleOnChangeBusinessLike}
        />
    );
};
